import React, {
  createContext,
  type ReactNode,
  useState,
  useCallback,
  useEffect,
  useContext,
  useRef,
} from 'react';
import * as Sentry from '@sentry/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { FirebaseError } from '@firebase/util';
import { db } from '../api/firebase/config';
import { AuthContext, type CompanyContextType } from './auth';
import { clearPatternListCache } from '../hooks/usePatternList';
import { isOperatorStaff } from '../utils/permission';
import CustomSnackbar from '../components/uiParts/form/CustomSnackbar';
import { PROXY_ALREADY_ACTIVE_MESSAGE, PROXY_COMPANY_NOT_FOUND_MESSAGE } from '../constants/labels';

const PROXY_LOGIN_STORAGE_KEY = 'proxyLoginState';
// 通常ログイン（非代理）時の詳細モード状態を保存する localStorage キー（#3069）。
// 代理モードの詳細状態（sessionStorage）とは別枠で持ち、端末単位の個人設定として永続化する。
const NORMAL_DETAIL_MODE_STORAGE_KEY = 'operatorDetailMode';
// URL から詳細モードを上書きするクエリパラメータ名（#3069）。
// 例: ?detail=0 で詳細OFFの画面をそのまま共有できる。
export const DETAIL_MODE_QUERY_KEY = 'detail';
// 代理ログイン対象の企業IDを保持するクエリパラメータ名（#3184）。
// 例: ?proxy=<companyId> で代理中のページURLをそのまま共有できる。
export const PROXY_COMPANY_QUERY_KEY = 'proxy';

type ProxyLoginState = {
  isProxyLogin: boolean;
  targetCompanyId: string | null;
  targetCompanyName: string | null;
  operatorId: string | null;
  operatorRole: number | null;
  isDetailMode: boolean;
};

type ProxyLoginContextType = ProxyLoginState & {
  isDetailMode: boolean;
  /**
   * URL の ?proxy= から代理モードを復元する処理が未完了かどうか（#3184）。
   * true の間は代理対象が未確定なため、各画面はデータ取得を待つ。
   * 待たずに取得すると、代理が確立する前に他社データへアクセスして権限エラーになる。
   */
  isProxyRestorePending: boolean;
  startProxyLogin: (
    companyId: string,
    companyName: string,
    operatorId: string,
    operatorRole: number,
  ) => void;
  endProxyLogin: () => void;
  toggleDetailMode: () => void;
};

const noop = (): void => void {};

// 通常ログイン時の詳細モードを localStorage から取得する（未設定時は ON）。
// 運営スタッフの従来の見え方（コスト・削除済みが見える状態）を初期値として維持する（#3069）。
const getStoredNormalDetailMode = (): boolean => {
  try {
    const stored = localStorage.getItem(NORMAL_DETAIL_MODE_STORAGE_KEY);
    if (stored !== null) {
      return JSON.parse(stored) as boolean;
    }
  } catch {
    // localStorage が使用できない場合やパースエラーは無視
  }
  return true;
};

const saveNormalDetailMode = (value: boolean): void => {
  try {
    localStorage.setItem(NORMAL_DETAIL_MODE_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // localStorage が使用できない場合は無視
  }
};

// URL の ?detail=0|1 を読み取る。指定がない・不正値の場合は null を返す（#3069）。
const getDetailModeFromUrl = (): boolean | null => {
  try {
    const value = new URLSearchParams(window.location.search).get(DETAIL_MODE_QUERY_KEY);
    if (value === '1') return true;
    if (value === '0') return false;
  } catch {
    // URL が解析できない環境では無視
  }
  return null;
};

// URL の ?proxy=<companyId> を読み取る。指定がない場合は null を返す（#3184）。
const getProxyCompanyIdFromUrl = (): string | null => {
  try {
    const value = new URLSearchParams(window.location.search).get(PROXY_COMPANY_QUERY_KEY);
    return value && value.trim() ? value : null;
  } catch {
    // URL が解析できない環境では無視
    return null;
  }
};

// アプリ起動時（このモジュールの評価時）の ?proxy= を一度だけ確保する（#3216）。
// ProxyLoginProvider は認証ゲート（MaintenanceGate ほか）の内側にあり、
// 一般ユーザーは MFA/SAML/IP制限の非同期確認を通るぶんマウントが遅れる。
// Provider のマウント時に URL を読むと、その間にクエリが落ちて復元判定を失うため、
// マウントタイミングに依存しないモジュールスコープで captured しておく。
const initialProxyCompanyIdFromUrl = getProxyCompanyIdFromUrl();

const getStoredState = (): ProxyLoginState | null => {
  try {
    const stored = sessionStorage.getItem(PROXY_LOGIN_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as ProxyLoginState;
    }
  } catch {
    // sessionStorage が使用できない場合やパースエラーは無視
  }
  return null;
};

const saveState = (state: ProxyLoginState): void => {
  try {
    sessionStorage.setItem(PROXY_LOGIN_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage が使用できない場合は無視
  }
};

const clearStoredState = (): void => {
  try {
    sessionStorage.removeItem(PROXY_LOGIN_STORAGE_KEY);
  } catch {
    // sessionStorage が使用できない場合は無視
  }
};

export const ProxyLoginContext = createContext<ProxyLoginContextType>({
  isProxyLogin: false,
  targetCompanyId: null,
  targetCompanyName: null,
  operatorId: null,
  operatorRole: null,
  isDetailMode: false,
  isProxyRestorePending: false,
  startProxyLogin: noop,
  endProxyLogin: noop,
  toggleDetailMode: noop,
});

// 対象企業のonSnapshotリスナーを設定し、AuthContext.companyをリアルタイム更新する
// onCompanyName: 企業名を呼び出し元へ通知する（URL復元時はバナー表示名がURLに無いため。#3184）
// onNotFound: 対象企業が存在しないことを呼び出し元へ通知する（#3203）
const setupCompanyListener = (
  companyId: string,
  setCurrentCompany: (company: CompanyContextType | null) => void,
  onCompanyName?: (companyName: string) => void,
  onNotFound?: () => void,
): (() => void) => {
  const companyDocRef = doc(db, 'companies', companyId);
  return onSnapshot(
    companyDocRef,
    snapshot => {
      if (!snapshot.exists()) {
        // 運営スタッフは存在しない企業ドキュメントも read が許可されるため（firestore.rules の isStaff）、
        // permission-denied ではなく exists=false の空スナップショットが成功として返る。
        // ここで握り潰すと代理先が未確定のまま画面だけ表示されてしまうため、呼び出し元へ通知する（#3203）。
        onNotFound?.();
        return;
      }

      const data = snapshot.data();
      onCompanyName?.((data.company_name as string) ?? '');
      setCurrentCompany({
        company_id: companyId,
        company_name: (data.company_name as string) ?? '',
        plan: (data.plan as number) ?? 0,
        ocr_monthly_budget: (data.ocr_monthly_budget as number) ?? 0,
        ocr_credit_balance: (data.ocr_credit_balance as number) ?? 0,
        current_pattern_count: (data.current_pattern_count as number) ?? 0,
        current_user_count: (data.current_user_count as number) ?? 0,
        user_limit: (data.user_limit as number) ?? 0,
        enable_master: (data.enable_master as boolean) ?? false,
        master_self_upload: (data.master_self_upload as boolean) ?? false,
        enable_dictionary: (data.enable_dictionary as boolean) ?? false,
        enable_export_template: (data.enable_export_template as boolean) ?? false,
        pattern_limit: (data.pattern_limit as number) ?? 1,
        master_record_limit: data.master_record_limit as number | undefined,
        webhook_enabled: (data.webhook_enabled as boolean) === true,
      });
    },
    error => {
      // ログアウト直後にリスナーが permission-denied を受け取るのは正常な動作
      if (error instanceof FirebaseError && error.code === 'permission-denied') {
        return;
      }
      Sentry.captureException(error);
    },
  );
};

export const ProxyLoginProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const { user, setCurrentCompany, refreshCompany, isLoading } = useContext(AuthContext);
  const companyUnsubscribeRef = useRef<(() => void) | null>(null);

  // 初期値は sessionStorage から復元
  const [isProxyLogin, setIsProxyLogin] = useState<boolean>(() => {
    const stored = getStoredState();
    return stored?.isProxyLogin ?? false;
  });
  const [targetCompanyId, setTargetCompanyId] = useState<string | null>(() => {
    const stored = getStoredState();
    return stored?.targetCompanyId ?? null;
  });
  const [targetCompanyName, setTargetCompanyName] = useState<string | null>(() => {
    const stored = getStoredState();
    return stored?.targetCompanyName ?? null;
  });
  const [operatorId, setOperatorId] = useState<string | null>(() => {
    const stored = getStoredState();
    return stored?.operatorId ?? null;
  });
  const [operatorRole, setOperatorRole] = useState<number | null>(() => {
    const stored = getStoredState();
    return stored?.operatorRole ?? null;
  });
  // 代理モード中の詳細モード（sessionStorage・初期 OFF）。顧客視点を再現するため既定は OFF。
  const [proxyDetailMode, setProxyDetailMode] = useState<boolean>(() => {
    const stored = getStoredState();
    return stored?.isDetailMode ?? false;
  });
  // 通常ログイン時の詳細モード（localStorage・初期 ON）。運営が任意に OFF にできる（#3069）。
  // URL に ?detail=0|1 があればそれを優先し、保存値より URL の指定を初期値とする。
  const [normalDetailMode, setNormalDetailMode] = useState<boolean>(
    () => getDetailModeFromUrl() ?? getStoredNormalDetailMode(),
  );
  // 現在有効な詳細モード。代理中は代理用、通常時は通常用の状態を参照する。
  const isDetailMode = isProxyLogin ? proxyDetailMode : normalDetailMode;
  const navigate = useNavigate();
  const location = useLocation();

  // URL の ?proxy= から代理モードを復元するための状態（#3184）。
  // 企業IDは「アプリ起動時のURL」から一度だけ確保する。認証確定（isLoading完了）や
  // 認証ゲートの通過を待つ間に、URL同期effectや各画面のリダイレクトでクエリが
  // 書き換わっても、復元判定を失わないようにする（#3216）。
  const proxyCompanyIdFromUrlRef = useRef<string | null>(initialProxyCompanyIdFromUrl);
  // 復元判定を打ち切ったか（拒否してホームへ退避した / 代理を終了した）
  const proxyRestoreHandledRef = useRef(false);
  // 復元待ちかどうか。URL に ?proxy= があるときだけ true で始まり、判定完了で false になる。
  // 各画面はこれが false になるまでデータ取得を待つ。
  const [isProxyRestorePending, setIsProxyRestorePending] = useState<boolean>(
    () => initialProxyCompanyIdFromUrl !== null,
  );
  // 代理の開始・終了で自前に遷移している途中の遷移先パス。
  // 到達するまでURL同期effectにURLを書き換えさせないために使う（#3184）。
  const pendingNavigationPathRef = useRef<string | null>(null);
  // 共有された ?proxy= 付きURLの異常系をユーザーへ知らせるメッセージ（#3203）。
  // 黙って状態を補正すると、バナーとURLの食い違いに気づけないため必ず通知する。
  const [proxyNoticeText, setProxyNoticeText] = useState<string>('');
  // 現在リスナーを張っている代理先の企業ID（#3203）。
  // 企業が存在しなかったときのコールバックが、張り替え後の新しい代理を
  // 誤って巻き戻さないよう、通知元が最新かどうかの判定に使う。
  const proxyTargetCompanyIdRef = useRef<string | null>(targetCompanyId);

  // 状態が変更されたら sessionStorage に保存
  useEffect(() => {
    if (isProxyLogin) {
      saveState({
        isProxyLogin,
        targetCompanyId,
        targetCompanyName,
        operatorId,
        operatorRole,
        isDetailMode: proxyDetailMode,
      });
    }
  }, [isProxyLogin, targetCompanyId, targetCompanyName, operatorId, operatorRole, proxyDetailMode]);

  // 通常ログイン時の詳細モードは localStorage に永続化する（#3069）
  useEffect(() => {
    saveNormalDetailMode(normalDetailMode);
  }, [normalDetailMode]);

  // 代理先の企業IDを ref にも保持する（#3203）。
  // リスナーのコールバックから「自分が最新の代理先か」を同期的に判定するために使う。
  useEffect(() => {
    proxyTargetCompanyIdRef.current = targetCompanyId;
  }, [targetCompanyId]);

  // 代理状態を破棄して初期化する共通処理（#3203）。
  // 代理の終了と、対象企業が存在しなかった場合の巻き戻しの両方から使う。
  const resetProxyState = useCallback((): void => {
    if (companyUnsubscribeRef.current) {
      companyUnsubscribeRef.current();
      companyUnsubscribeRef.current = null;
    }
    clearPatternListCache();
    setIsProxyLogin(false);
    setTargetCompanyId(null);
    setTargetCompanyName(null);
    setOperatorId(null);
    setOperatorRole(null);
    setProxyDetailMode(false);
    clearStoredState();
  }, []);

  // 対象企業が存在しなかったときの共通処理（#3203）。
  // URL復元・sessionStorage復元のどちらのリスナーからも同じ挙動にするため ref に持つ。
  // ref にするのは、リスナーを張る時点でこの関数が未定義でも参照できるようにするため。
  const handleCompanyNotFoundRef = useRef<(companyId: string) => void>(() => undefined);
  handleCompanyNotFoundRef.current = (companyId: string): void => {
    // 張り替え後の新しい代理を、古いリスナーの通知で壊さない
    if (proxyTargetCompanyIdRef.current !== companyId) return;
    Sentry.captureMessage('代理ログイン: 存在しない企業IDが指定されたため中止', {
      level: 'warning',
      extra: { companyId },
    });
    resetProxyState();
    // 運営スタッフ自身の企業情報へ戻す（#3326）。
    // リスナー解除後に呼ぶことで、解除前の最後の通知に上書きされないようにする。
    refreshCompany();
    // 復元effectに再試行させない（?proxy= は残っているため、消さないと復元が繰り返される）
    proxyCompanyIdFromUrlRef.current = null;
    proxyRestoreHandledRef.current = true;
    proxyTargetCompanyIdRef.current = null;
    setProxyNoticeText(PROXY_COMPANY_NOT_FOUND_MESSAGE);
    pendingNavigationPathRef.current = '/home';
    navigate('/home', { replace: true });
  };

  // 代理モードの状態を確立する共通処理（#3184）。
  // 画面遷移は行わないため、企業一覧からの開始と URL 復元の双方から使える。
  // 権限チェックは呼び出し元のUIガードに加えた深層防御。
  const applyProxyLogin = useCallback(
    (companyId: string, companyName: string, opId: string, opRole: number): boolean => {
      if (!isOperatorStaff(opRole)) {
        Sentry.captureMessage('applyProxyLogin: 運営スタッフ以外による代理ログイン試行を拒否', {
          level: 'warning',
          extra: { opId, opRole, companyId },
        });
        return false;
      }

      // 代理モード開始時にパターンリストのキャッシュをクリア
      clearPatternListCache();

      // 前のリスナーをクリーンアップ
      if (companyUnsubscribeRef.current) {
        companyUnsubscribeRef.current();
        companyUnsubscribeRef.current = null;
      }

      // これから張るリスナーの対象を先に記録する（#3203）。
      // onSnapshot はこの直後にも発火しうるため、state 更新用の effect を待っていると
      // コールバック側の「自分が最新の代理先か」の判定が古い値のままになり、
      // 企業が存在しないことを検知できない。
      proxyTargetCompanyIdRef.current = companyId;

      // リアルタイムリスナーを設定（onSnapshotは初回接続時にもコールバックを発火するため、
      // getCompanyによる初期データ取得は不要）
      // URL復元時は企業名がURLに無いため、リスナーが取得した企業名でバナー表示を補完する（#3184）
      companyUnsubscribeRef.current = setupCompanyListener(
        companyId,
        setCurrentCompany,
        name => setTargetCompanyName(name),
        // 対象企業が存在しない場合は代理を成立させない（#3203）。
        // 巻き戻さずに留まると、代理先が未確定のまま他社IDでデータ取得を試み続けてしまう。
        () => handleCompanyNotFoundRef.current(companyId),
      );

      setIsProxyLogin(true);
      setTargetCompanyId(companyId);
      setTargetCompanyName(companyName);
      setOperatorId(opId);
      setOperatorRole(opRole);
      return true;
    },
    [setCurrentCompany, resetProxyState, navigate],
  );

  const startProxyLogin = useCallback(
    (companyId: string, companyName: string, opId: string, opRole: number) => {
      if (!applyProxyLogin(companyId, companyName, opId, opRole)) return;
      // 遷移先URLに最初から ?proxy= を付ける（#3184）。
      // あわせて遷移先を記録し、到達するまでURL同期effectに書き換えさせない
      // （effect実行時の location はまだ遷移前のパスのため、元の画面へ差し戻されてしまう）。
      pendingNavigationPathRef.current = '/home';
      navigate(`/home?${PROXY_COMPANY_QUERY_KEY}=${encodeURIComponent(companyId)}`);
    },
    [applyProxyLogin, navigate],
  );

  const endProxyLogin = useCallback(() => {
    const companyId = targetCompanyId;

    // リスナー解除・キャッシュクリア・代理stateの初期化
    resetProxyState();

    // 代理先の企業情報から運営スタッフ自身の企業情報へ戻す（#3326）。
    // null のままにすると、ホームなど AuthContext.company を参照する画面が
    // リロードするまで「企業情報の取得に失敗しました」になってしまう。
    refreshCompany();
    // URL由来の復元情報を破棄する（#3184）。
    // 残したままだと、代理を終了した直後に復元effectが再び代理状態に戻してしまい終了できない。
    proxyCompanyIdFromUrlRef.current = null;
    proxyRestoreHandledRef.current = true;
    // 代理を終了したら、切り替え不可の警告は用済みなので消す（#3203）
    setProxyNoticeText('');

    // 代理を終了したら、どの画面からでも運営専用の企業詳細へ戻す。
    // 遷移先を記録し、到達するまでURL同期effectに書き換えさせない（startProxyLogin と同様）。
    if (companyId) {
      pendingNavigationPathRef.current = `/admin/company/${companyId}`;
      navigate(`/admin/company/${companyId}`);
      return;
    }
    // 対象企業が不明な場合でも代理中の画面には留まらせず、企業一覧へ戻す
    pendingNavigationPathRef.current = '/admin/company';
    navigate('/admin/company');
  }, [targetCompanyId, navigate, refreshCompany, resetProxyState]);

  // 代理中は代理用、通常時は通常用の詳細モードを切り替える（#3069）
  const toggleDetailMode = useCallback(() => {
    if (isProxyLogin) {
      setProxyDetailMode(prev => !prev);
      return;
    }
    setNormalDetailMode(prev => !prev);
  }, [isProxyLogin]);

  // sessionStorage から復元時に対象企業のcompanyデータも復元
  // isLoadingを監視し、認証完了後にリスナーを設定する
  // （auth.tsxのonAuthStateChangedが運営スタッフのcompanyをnullに設定した後に実行するため）
  useEffect(() => {
    if (isLoading) return;
    // 未認証時はリスナーを張らない（ログアウト直後のレンダリングで誤って張るのを防止）
    if (!user) return;
    if (isProxyLogin && targetCompanyId) {
      // 既に同じ企業のリスナーが張られていれば張り直さない（#3203）。
      // applyProxyLogin が「企業が存在しないことを検知するコールバック」付きで張った直後に
      // このeffectが上書きすると、存在チェックが効かなくなる。
      if (proxyTargetCompanyIdRef.current === targetCompanyId && companyUnsubscribeRef.current) {
        return;
      }
      if (companyUnsubscribeRef.current) {
        companyUnsubscribeRef.current();
        companyUnsubscribeRef.current = null;
      }
      proxyTargetCompanyIdRef.current = targetCompanyId;
      // リアルタイムリスナーを設定（onSnapshotは初回接続時にもコールバックを発火するため、
      // getCompanyによる初期データ取得は不要）
      // sessionStorage 由来の復元でも、企業が消えている可能性があるため存在チェックを行う（#3203）
      companyUnsubscribeRef.current = setupCompanyListener(
        targetCompanyId,
        setCurrentCompany,
        name => setTargetCompanyName(name),
        () => handleCompanyNotFoundRef.current(targetCompanyId),
      );
    }
  }, [user, isLoading, isProxyLogin, targetCompanyId, setCurrentCompany]);

  // URL の ?proxy=<companyId> から代理モードを復元する（#3184）。
  // 代理中にコピーしたURLを別タブ・別の運営スタッフが開いても同じページを表示できるようにする。
  // 運営スタッフでない場合は復元しない（URLを知っていても他社データは開けない）。
  useEffect(() => {
    if (isLoading) return;
    if (!user) {
      // 未認証が確定したら待たせない（ログイン画面へのリダイレクトを妨げないため）
      setIsProxyRestorePending(false);
      return;
    }
    // 既に代理中なら切り替えない（別企業への切り替えは企業一覧から明示的に行う）。
    // ただし黙って無視すると、バナー（代理中の企業）とURL（リンク先の企業）が
    // 食い違ったまま気づけないため、別企業のリンクだった場合は警告する（#3203）。
    if (isProxyLogin) {
      const requestedCompanyId = proxyCompanyIdFromUrlRef.current;
      if (requestedCompanyId && requestedCompanyId !== targetCompanyId) {
        setProxyNoticeText(PROXY_ALREADY_ACTIVE_MESSAGE);
        // 判定済みにして、以降のレンダーで警告が繰り返し立ち上がらないようにする
        proxyCompanyIdFromUrlRef.current = null;
        // 現在の代理先（A社）のホームへ退避させる（#3203）。
        // ?proxy= を A社に直すだけでは、パスがリンク先（B社）のページのまま残るため、
        // 「ヘッダー・サイドバーはA社、中身はB社のパターン/結果」という食い違いが起きる。
        // A社の代理でB社のIDを開いている状態になり、権限エラーや空表示の原因にもなる。
        pendingNavigationPathRef.current = '/home';
        navigate('/home', { replace: true });
      }
      setIsProxyRestorePending(false);
      return;
    }

    const companyIdFromUrl = proxyCompanyIdFromUrlRef.current;
    if (!companyIdFromUrl) {
      setIsProxyRestorePending(false);
      return;
    }

    // 復元を打ち切った後（拒否してホームへ退避 / 代理を終了）は再度復元しない。
    // 成功時にこのフラグを立てないのは、StrictMode の effect 二重実行で
    // 1回目の state 更新が破棄されたまま2回目がスキップされ、代理が確立しなくなるため。
    // 成功したかどうかは上の isProxyLogin（state）で判定する。
    if (proxyRestoreHandledRef.current) return;

    const role = user.role;
    if (role === null || role === undefined || !isOperatorStaff(role)) {
      Sentry.captureMessage('URL復元: 運営スタッフ以外による代理ログイン試行を拒否', {
        level: 'warning',
        extra: { uid: user.uid, role, companyId: companyIdFromUrl },
      });
      // 代理にならないまま元のページに留まると、他社データを取得できず
      // ローディングが終わらない画面で固まってしまう。
      // ?proxy= を取り除いたうえでホームへ退避させる（#3184）。
      // 退避は一度きりにするためここでフラグを立てる。
      proxyRestoreHandledRef.current = true;
      proxyCompanyIdFromUrlRef.current = null;
      // 遷移が完了するまでURL同期effectにURLを書き換えさせない（#3216）。
      // これがないと、遷移前のパスを見た同期effectが ?proxy= 削除の replace を打ち、
      // /home への遷移を打ち消してしまう（③の代理中切り替えと同じ理由）。
      pendingNavigationPathRef.current = '/home';
      navigate('/home', { replace: true });
      setIsProxyRestorePending(false);
      return;
    }

    // 企業名は URL に含まれないため空で開始し、リスナー取得後に補完される
    applyProxyLogin(companyIdFromUrl, '', user.uid, role);
    setIsProxyRestorePending(false);
  }, [user, isLoading, isProxyLogin, targetCompanyId, applyProxyLogin, navigate]);

  // 代理中は全ページのURLに ?proxy=<companyId> を反映する（#3184）。
  // これにより、代理中にアドレスバーからコピーしたURLがそのまま共有できる。
  // 代理を終了したらパラメータを取り除く。履歴を汚さないよう replace で書き換える。
  //
  // 注意1: 認証確定前（isLoading中）は ?proxy= を絶対に消さない。
  // 初回マウント時点では復元effectがまだ動いておらず isProxyLogin=false のため、
  // ここで消すと復元に必要なパラメータを自分で破壊してしまう（共有URLが開けなくなる）。
  //
  // 注意2: 代理の開始・終了で自前の遷移が進行中の間は書き換えない。
  // state（isProxyLogin）は即座に変わる一方 location は遷移完了まで前のパスのままのため、
  // ここで追いかけると遷移前のパスへ差し戻してしまう（例: /home へ遷移できず企業詳細に留まる）。
  useEffect(() => {
    // 認証確定前は何もしない（復元effectが動く前にパラメータを消さないため）
    if (isLoading) return;
    // 復元判定が終わるまで触らない（復元前にパラメータを消さないため）。
    // 判定が終われば代理中は付与・非代理中は削除を通常どおり行う。
    if (isProxyRestorePending) return;
    // 自前の遷移が完了するまで待つ（遷移先に到達したら解除する）
    if (pendingNavigationPathRef.current) {
      if (location.pathname !== pendingNavigationPathRef.current) return;
      pendingNavigationPathRef.current = null;
    }

    const params = new URLSearchParams(location.search);
    const current = params.get(PROXY_COMPANY_QUERY_KEY);
    const expected = isProxyLogin ? targetCompanyId : null;

    if (current === expected) return;

    // 未認証のうちは ?proxy= を保持する（ログイン後に復元させるため）
    if (!expected && !user) return;

    if (expected) {
      params.set(PROXY_COMPANY_QUERY_KEY, expected);
    } else {
      params.delete(PROXY_COMPANY_QUERY_KEY);
    }
    const query = params.toString();
    // 遷移元が渡した state を引き継ぐ（#3410）。
    // 代理中は ?proxy= の無いパスへ遷移すると必ずここで書き換わるため、
    // state を渡さないと遷移先で受け取れなくなる（例: パターン一覧の「編集」で子パターンのモーダルが開かない）
    navigate(`${location.pathname}${query ? `?${query}` : ''}${location.hash}`, {
      replace: true,
      state: location.state,
    });
  }, [
    isProxyLogin,
    targetCompanyId,
    isLoading,
    isProxyRestorePending,
    user,
    location.pathname,
    location.search,
    location.hash,
    location.state,
    navigate,
  ]);

  // ログアウト時の代理モード自動クリーンアップ
  // 通常のログアウトボタン押下に加え、セッション切れ・別タブログアウト等の全経路で発火する。
  // sessionStorage を残したままだと、F5や別タブで代理状態が復元されてしまうため明示的にクリアする。
  useEffect(() => {
    if (isLoading) return;
    if (!user && isProxyLogin) {
      if (companyUnsubscribeRef.current) {
        companyUnsubscribeRef.current();
        companyUnsubscribeRef.current = null;
      }
      clearPatternListCache();
      setIsProxyLogin(false);
      setTargetCompanyId(null);
      setTargetCompanyName(null);
      setOperatorId(null);
      setOperatorRole(null);
      setProxyDetailMode(false);
      clearStoredState();
    }
  }, [user, isLoading, isProxyLogin]);

  // アンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      if (companyUnsubscribeRef.current) {
        companyUnsubscribeRef.current();
      }
    };
  }, []);

  return (
    <ProxyLoginContext.Provider
      value={{
        isProxyLogin,
        targetCompanyId,
        targetCompanyName,
        operatorId,
        operatorRole,
        isDetailMode,
        isProxyRestorePending,
        startProxyLogin,
        endProxyLogin,
        toggleDetailMode,
      }}
    >
      {children}
      {/*
        共有された ?proxy= 付きURLの異常系を通知する（#3203）。
        Provider 直下に置くことで、どのページを開いていても同じ導線で伝えられる。
      */}
      <CustomSnackbar
        open={proxyNoticeText !== ''}
        text={proxyNoticeText}
        onClose={(): void => setProxyNoticeText('')}
        closeButton={(): void => setProxyNoticeText('')}
      />
    </ProxyLoginContext.Provider>
  );
};
